import { useEffect, useState } from "react";
import { DashboardService } from "../../dashboard/services";
import type { IDashboardData, ISearchTermsGrowth } from "../../dashboard/types";
import { RelatorioService } from "../services";
import type { IHistoricoDePosicoes } from "../types";

export type EstadoDoRelatorio = {
  perfil: IDashboardData | null;
  pesquisas: ISearchTermsGrowth | null;
  historico: IHistoricoDePosicoes | null;
  carregando: boolean;
  carregandoPesquisas: boolean;
  falhou: boolean;
};

const STATUS_EM_PREPARACAO = [
  "LOGIN_PENDING",
  "WAITING_FIRST_PAYMENT",
  "WAITING_INITIAL_SYNC",
  "PENDING_INITIAL_PROFILE_UPDATE",
];

const JANELA_BASE = 365;

export const emPreparacao = (perfil: IDashboardData | null): boolean =>
  !!perfil && STATUS_EM_PREPARACAO.includes(perfil.status);

const valido = (dados: unknown): IDashboardData | null =>
  dados && typeof dados === "object" && !Array.isArray(dados) ? (dados as IDashboardData) : null;

/**
 * O endpoint do perfil filtra posts pelo período pedido, então o relatório de
 * 30 dias vinha sem nenhum impulsionamento quando o último era mais antigo.
 * Buscamos uma janela de 1 ano para posts e avaliações e a janela escolhida
 * só para as métricas do Google.
 */
export const useRelatorio = (
  profileId: string,
  dias: number,
  opcoes: { historico?: boolean; mesesDePesquisa?: number } = {},
): EstadoDoRelatorio => {
  const [perfil, setPerfil] = useState<IDashboardData | null>(null);
  const [pesquisas, setPesquisas] = useState<ISearchTermsGrowth | null>(null);
  const [historico, setHistorico] = useState<IHistoricoDePosicoes | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [carregandoPesquisas, setCarregandoPesquisas] = useState(true);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setFalhou(false);
    const periodo = DashboardService.getProfile(profileId, dias);
    const base = dias === JANELA_BASE ? periodo : DashboardService.getProfile(profileId, JANELA_BASE);
    Promise.all([periodo, base.catch(() => null)])
      .then(([doPeriodo, daBase]) => {
        if (!ativo) return;
        const atual = valido(doPeriodo);
        const anual = valido(daBase);
        if (!atual) {
          setPerfil(null);
          setFalhou(true);
          return;
        }
        setPerfil(
          anual
            ? { ...atual, posts: anual.posts ?? atual.posts, reviews: anual.reviews ?? atual.reviews }
            : atual,
        );
      })
      .catch(() => {
        if (ativo) setFalhou(true);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [profileId, dias]);

  useEffect(() => {
    let ativo = true;
    setCarregandoPesquisas(true);
    DashboardService.getSearchTermsGrowth(profileId, opcoes.mesesDePesquisa ?? 12)
      .then((dados) => {
        if (ativo) setPesquisas(dados ?? null);
      })
      .catch(() => {
        if (ativo) setPesquisas(null);
      })
      .finally(() => {
        if (ativo) setCarregandoPesquisas(false);
      });
    return () => {
      ativo = false;
    };
  }, [profileId, opcoes.mesesDePesquisa]);

  useEffect(() => {
    if (!opcoes.historico) return undefined;
    let ativo = true;
    RelatorioService.getHistoricoDePosicoes(profileId, 12)
      .then((dados) => {
        if (ativo) setHistorico(dados ?? null);
      })
      .catch(() => {
        if (ativo) setHistorico(null);
      });
    return () => {
      ativo = false;
    };
  }, [profileId, opcoes.historico]);

  return { perfil, pesquisas, historico, carregando, carregandoPesquisas, falhou };
};
